import { ExportFormatCode } from './practice.models';

/** Id/name pair for the management reporting pick lists. */
export interface ManagementOption {
  id: number;
  name: string;
}

export interface ManagementReportingOptions {
  periodTypes: ManagementOption[];
  statuses: ManagementOption[];
  dimensionKinds: ManagementOption[];
  packSections: string[];
}

/** Profit and loss subtotals in presentation sign: income and costs are positive, profit is income - costs. */
export interface ManagementTotals {
  income: number;
  costs: number;
  profit: number;
}

// ---- Management periods ----

export interface ManagementPeriodRow {
  id: string;
  clientId: string;
  accountingPeriodId: string | null;
  name: string;
  periodType: number;
  periodTypeName: string;
  sequenceNo: number;
  startDate: string;
  endDate: string;
  status: number;
  statusName: string;
  trialBalanceId: string | null;
  trialBalanceRef: string | null;
  snapshotAtUtc: string | null;
  snapshotLineCount: number;
}

export interface SaveManagementPeriodPayload {
  id?: string | null;
  clientId: string;
  accountingPeriodId?: string | null;
  name: string;
  periodType: number;
  startDate: string;
  endDate: string;
  trialBalanceId?: string | null;
}

export interface GenerateManagementPeriodsPayload {
  clientId: string;
  accountingPeriodId?: string | null;
  periodType: number;
  startDate: string;
  count: number;
}

export interface GenerateManagementPeriodsResult {
  created: number;
  periods: ManagementPeriodRow[];
}

export interface SnapshotResult {
  period: ManagementPeriodRow;
  lineCount: number;
}

// ---- Periodic trial balance ----

export interface PeriodicBalanceRow {
  accountCode: string;
  accountName: string | null;
  accountTypeId: number | null;
  accountTypeName: string | null;
  groupName: string | null;
  statementArea: number | null;
  dimensionValue: string;
  debit: number;
  credit: number;
  amount: number;
}

export interface PeriodicTrialBalanceResponse {
  period: ManagementPeriodRow;
  dimensionCode: string | null;
  dimensionValue: string | null;
  rows: PeriodicBalanceRow[];
  totalDebit: number;
  totalCredit: number;
  totals: ManagementTotals;
}

// ---- Variance ----

export interface VarianceRow {
  accountCode: string;
  accountName: string | null;
  accountTypeName: string | null;
  groupName: string | null;
  statementArea: number | null;
  dimensionValue: string;
  currentAmount: number;
  priorAmount: number;
  budgetAmount: number;
  budgetVariance: number;
  budgetVariancePercent: number | null;
  priorVariance: number;
  priorVariancePercent: number | null;
}

export interface VarianceReportResponse {
  managementPeriodId: string;
  clientId: string;
  clientName: string;
  periodName: string;
  startDate: string;
  endDate: string;
  priorManagementPeriodId: string | null;
  priorPeriodName: string | null;
  dimensionCode: string | null;
  dimensionValue: string | null;
  rows: VarianceRow[];
  currentTotals: ManagementTotals;
  priorTotals: ManagementTotals;
  budgetTotals: ManagementTotals;
  profitBudgetVariance: number;
  profitBudgetVariancePercent: number | null;
  profitPriorVariance: number;
  profitPriorVariancePercent: number | null;
}

// ---- Multi-year ----

export interface MultiYearColumn {
  yearOffset: number;
  managementPeriodId: string | null;
  name: string;
  startDate: string | null;
  endDate: string | null;
}

export interface MultiYearRow {
  accountCode: string;
  accountName: string | null;
  accountTypeName: string | null;
  groupName: string | null;
  statementArea: number | null;
  /** Positionally aligned with the report's columns. */
  amounts: number[];
}

export interface MultiYearReportResponse {
  managementPeriodId: string;
  clientId: string;
  clientName: string;
  periodName: string;
  dimensionCode: string | null;
  dimensionValue: string | null;
  columns: MultiYearColumn[];
  rows: MultiYearRow[];
  totals: ManagementTotals[];
}

// ---- Budgets ----

export interface BudgetRow {
  accountCode: string;
  accountName: string | null;
  accountTypeId: number | null;
  accountTypeName: string | null;
  groupName: string | null;
  statementArea: number | null;
  dimensionCode: string;
  dimensionValue: string;
  budgetAmount: number;
  actualAmount: number;
  variance: number;
  variancePercent: number | null;
  note: string | null;
}

export interface BudgetGridResponse {
  period: ManagementPeriodRow;
  dimensionCode: string | null;
  dimensionValue: string | null;
  rows: BudgetRow[];
}

export interface BudgetLineInput {
  accountCode: string;
  budgetAmount: number;
  note?: string | null;
}

export interface SaveBudgetsPayload {
  managementPeriodId: string;
  dimensionCode?: string | null;
  dimensionValue?: string | null;
  replaceAll: boolean;
  lines: BudgetLineInput[];
}

export interface DeleteBudgetsPayload {
  managementPeriodId: string;
  dimensionCode?: string | null;
  dimensionValue?: string | null;
  accountCodes: string[];
}

// ---- Dimensions ----

export interface DimensionValueRow {
  id: string;
  dimensionId: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface DimensionRow {
  id: string;
  clientId: string | null;
  code: string;
  name: string;
  kind: number;
  kindName: string;
  isActive: boolean;
  values: DimensionValueRow[];
}

export interface SaveDimensionPayload {
  id?: string | null;
  clientId?: string | null;
  code: string;
  name: string;
  kind: number;
  isActive: boolean;
}

export interface SaveDimensionValuePayload {
  id?: string | null;
  dimensionId: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface CaptureDimensionsResult {
  captured: number;
  dimensions: DimensionRow[];
}

// ---- Packs ----

export interface ManagementPackRow {
  id: string;
  clientId: string;
  clientName: string;
  name: string;
  fromManagementPeriodId: string;
  fromPeriodName: string;
  fromStartDate: string;
  toManagementPeriodId: string;
  toPeriodName: string;
  toEndDate: string;
  showPrior: boolean;
  showBudget: boolean;
  showVariance: boolean;
  multiYearCount: number;
  dimensionCode: string | null;
  sections: string[];
  updatedAtUtc: string;
}

export interface SaveManagementPackPayload {
  id?: string | null;
  clientId: string;
  name: string;
  fromManagementPeriodId: string;
  toManagementPeriodId: string;
  showPrior: boolean;
  showBudget: boolean;
  showVariance: boolean;
  multiYearCount: number;
  dimensionCode?: string | null;
  sections: string[];
}

// ---- Exports ----

export interface ManagementExportQuery {
  format: ExportFormatCode;
  isPrint?: boolean;
  dimensionCode?: string | null;
  dimensionValue?: string | null;
  priorManagementPeriodId?: string | null;
  years?: number;
}

/** Amounts print with two decimals and a dash for nothing, as the exports do. */
export function managementMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) {
    return '-';
  }
  return value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function managementPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '-';
  }
  return `${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

/** Dates reach the API as yyyy-MM-dd; the pickers hand back a Date or a Moment. */
export function toIsoDateString(value: any): string {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value.length > 10 ? value.substring(0, 10) : value;
  }
  const date: Date = typeof value.toDate === 'function' ? value.toDate() : value;
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The section keys a management report pack can print, with the labels the screen shows. */
export const PACK_SECTIONS: { key: string; name: string }[] = [
  { key: 'periodic-tb', name: 'Periodic trial balance' },
  { key: 'variance', name: 'Current, prior and budget variance' },
  { key: 'multi-year', name: 'Multi-year comparison' },
  { key: 'dimension', name: 'Dimension analysis' },
];
